/* eslint-disable @typescript-eslint/no-unused-vars */
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  AlertsArticlesRequest, AlertsResponse, Company, NotificationAlertRequest,
  ExportNotificationHighlightsRequest,
  ExportNotificationHighlightsResponse,
  NotificationHighlightsRequest,
  NotificationHighlightsResponse,
  NotificationOpenedRequest, NotificationResponse,
  DynamicHighlightResponse,
  NotificationPulseRequest,
  NotificationPulseResponse
} from 'Types/CompaniesTypes';
import { AlertsState } from 'Types/GlobalTypes';
import { AddToFavoriteRequest } from 'Types/RequestBodies';

export const initialState: AlertsState = {
  articles: [],
  articlesLoading: false,
  toggle: false,
  articlesPage: 1,
  articlesEnded: false,
  notificationPulse: [],
  notificationPulseLoading: false,
  selectedFilters: [],
  selectedPeopleFilters: [],
  selectedCompanies: [],
  unSelectedCompanies: [],
  selectAllCompanies: true,
  openDrawer: false,
  notificationsLoading: false,
  peopleNotifications: [],
  notificationsPage: 1,
  notificationsEnded: false,
  openedLoading: false,
  peopleMessage: '',
  notes: [],
  notesEnded: false,
  notesLoading: false,
  notesPage: 1,
  notesMessage: '',
  selectedNotesFilters: [],
  // Feed States
  feedPeopleNotifications: [],
  feedNotificationsEnded: false,
  feedNotificationsPage: 1,
  feedNotificationsLoading: false,
  feedPeopleMessage: '',
  feedNotificationHighlights: {} as DynamicHighlightResponse,
  feedNotificationHighlightsLoading: false,
  exportNotificationHighlightsLoading: false,
};

const AlertsSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {
    toggleScreen(state, action: PayloadAction<boolean>) {
      state.toggle = action?.payload;
    },
    getAlertArticles(state, action: PayloadAction<AlertsArticlesRequest>) {
      const { payload } = action;
      if (payload.page === 1) {
        if (payload.filter?.length > 0) {
          state.selectedFilters = payload?.filter;
        }
        state.articles = [];
        // state.notificationPulse = [];
        state.articlesPage = 1;
      }
      state.articlesLoading = true;
      state.articlesEnded = false;
    },
    setMoreAlertArticles(state, action: PayloadAction<AlertsResponse>) {
      const { payload } = action;
      state.articlesPage += 1;
      state.articles = state.articles.concat(payload?.articles);
    },
    setArticlesEnded(state) {
      state.articlesEnded = true;
      state.articlesLoading = false;
    },
    setAlertArticles(state, action: PayloadAction<AlertsResponse>) {
      const { payload } = action;
      state.articlesLoading = false;
      state.articles = payload?.articles;
      // state.notificationPulse = payload?.notificationPulse;
      state.articlesPage += 1;
    },
    getNotificationPulse(state, action: PayloadAction<NotificationPulseRequest>) {
      state.notificationPulseLoading = true;
      state.notificationPulse = [];
    },
    setNotificationPulse(state, action: PayloadAction<NotificationPulseResponse>) {
      state.notificationPulseLoading = false;
      state.notificationPulse = action.payload?.notificationPulse;
    },
    setUnSelectedAll(state, action: PayloadAction<boolean>) {
      state.selectAllCompanies = action.payload;
    },
    setUnSelectedCompanies(state, action: PayloadAction<Company[]>) {
      const { payload } = action;
      if (payload.length < 1) state.unSelectedCompanies = [];
      else { state.unSelectedCompanies = payload; }
    },
    setSelectedCompanies(state, action: PayloadAction<Company[]>) {
      const { payload } = action;
      if (payload.length < 1) state.selectedCompanies = [];
      else { state.selectedCompanies = payload; }
    },
    handleCompanyUnfollow(state, action: PayloadAction<string[]>) {
      const companiesToRemoveIds = action.payload.map(Number);
      state.unSelectedCompanies = state.unSelectedCompanies.filter(
        (company) => !companiesToRemoveIds.includes(company.companyId),
      );
      state.selectedCompanies = state.selectedCompanies.filter(
        (company) => !companiesToRemoveIds.includes(company.companyId),
      );
    },
    isReadAlert(state, action: PayloadAction<number>) {
      const { payload } = action;
      state.articles.forEach((item) => {
        const article = item;
        if (article.articleId === payload) {
          article.isRead = true;
        }
      });
    },
    setDrawerState(state) {
      state.openDrawer = true;
    },
    addToFavoriteSuccess(state, action: PayloadAction<AddToFavoriteRequest>) {
      const { articleId, favoriteFlag } = action.payload;
      if (articleId) {
        if (state.articles && state.articles.length) {
          const matchedArticles = state.articles.filter(
            (article) => article.articleId === articleId,
          );
          if (matchedArticles.length > 0) {
            matchedArticles.forEach((arrayItem) => {
              const article = arrayItem;
              article.isFavorite = favoriteFlag === 1;
            });
          }
        }
      }
    },
    getNotificationAlerts(state, action: PayloadAction<NotificationAlertRequest>) {
      const { payload } = action;
      if (payload.isNotes) {
        if (payload.page === 1) {
          if (payload.filter?.length > 0) {
            state.selectedNotesFilters = payload?.filter;
          }
          state.notes = [];
          state.notesLoading = true;
          state.notesPage = 1;
          state.notesMessage = '';
        }
        state.notesEnded = false;
      } else {
        if (payload.page === 1) {
          if (payload.filter?.length > 0) {
            state.selectedPeopleFilters = payload?.filter;
          }
          state.notificationsLoading = true;
          state.peopleNotifications = [];
          state.notificationsPage = 1;
          state.peopleMessage = '';
        }
        state.notificationsEnded = false;
      }
    },
    getFeedNotificationAlerts(state, action: PayloadAction<NotificationAlertRequest>) {
      const { payload } = action;
      if (payload.page === 1) {
        if (payload.filter?.length > 0) {
          state.selectedPeopleFilters = payload?.filter;
        }
        state.feedNotificationsLoading = true;
        state.feedPeopleNotifications = [];
        state.feedNotificationsPage = 1;
        state.feedPeopleMessage = '';
      }
      state.feedNotificationsEnded = false;
    },
    setNotificationAlerts(state, action: PayloadAction<NotificationResponse>) {
      const { payload } = action;
      state.notificationsLoading = false;
      state.peopleNotifications = payload?.notifications;
      state.notificationsPage += 1;
    },
    setMoreNotificationAlerts(state, action: PayloadAction<NotificationResponse>) {
      const { payload } = action;
      state.notificationsPage += 1;
      state.peopleNotifications = state.peopleNotifications.concat(payload?.notifications);
    },
    setFeedNotificationAlerts(state, action: PayloadAction<NotificationResponse>) {
      const { payload } = action;
      state.feedNotificationsLoading = false;
      state.feedPeopleNotifications = payload?.notifications;
      state.feedNotificationsPage += 1;
    },
    setFeedMoreNotificationAlerts(state, action: PayloadAction<NotificationResponse>) {
      const { payload } = action;
      state.feedNotificationsPage += 1;
      state.feedPeopleNotifications = state.feedPeopleNotifications.concat(payload?.notifications);
    },
    setNotificationMessage(state, action: PayloadAction<string>) {
      state.notificationsLoading = false;
      state.notificationsEnded = true;
      state.peopleMessage = action.payload;
    },
    setFeedNotificationMessage(state, action: PayloadAction<string>) {
      state.feedNotificationsLoading = false;
      state.feedNotificationsEnded = true;
      state.feedPeopleMessage = action.payload;
    },
    setNotificationAlertsEnded(state) {
      state.notificationsEnded = true;
      state.notificationsLoading = false;
    },
    setFeedNotificationAlertsEnded(state) {
      state.feedNotificationsEnded = true;
      state.feedNotificationsLoading = false;
    },
    notificationAlertsError(state) {
      state.notificationsLoading = false;
      state.notificationsEnded = true;
    },
    feedNotificationAlertsError(state) {
      state.feedNotificationsLoading = false;
      state.feedNotificationsEnded = true;
    },
    getNotificationOpened(state, action: PayloadAction<NotificationOpenedRequest>) {
      state.openedLoading = true;
    },
    getFeedNotificationOpened(state, action: PayloadAction<NotificationOpenedRequest>) {
      state.openedLoading = true;
    },
    getNotificationHighlights(state, action: PayloadAction<NotificationHighlightsRequest>) {
      state.feedNotificationHighlightsLoading = true;
    },
    setNotificationHighlights(state, action: PayloadAction<NotificationHighlightsResponse>) {
      state.feedNotificationHighlightsLoading = false;
      state.feedNotificationHighlights = action.payload?.response;
    },
    exportNotificationHighlights(state, action: PayloadAction<ExportNotificationHighlightsRequest>) {
      state.exportNotificationHighlightsLoading = true;
    },
    setExportNotificationHighlights(state, action: PayloadAction<ExportNotificationHighlightsResponse>) {
      state.exportNotificationHighlightsLoading = false;
    },
    exportNotificationHighlightsError(state) {
      state.exportNotificationHighlightsLoading = false;
    },
    setNotificationOpened(state, action: PayloadAction<number>) {
      const { payload } = action;
      const objIndex = state.peopleNotifications?.findIndex(
        (notification) => notification?.id === payload,
      );
      if (objIndex) { state.peopleNotifications[objIndex].readFlag = true; }
      state.openedLoading = false;
    },
    setFeedNotificationOpened(state, action: PayloadAction<number>) {
      const { payload } = action;
      const objIndex = state.peopleNotifications?.findIndex(
        (notification) => notification?.id === payload,
      );
      if (objIndex) { state.peopleNotifications[objIndex].readFlag = true; }
      state.openedLoading = false;
    },
    setNotesAlerts(state, action: PayloadAction<NotificationResponse>) {
      const { payload } = action;
      state.notesLoading = false;
      state.notes = payload?.notifications;
      state.notesPage += 1;
    },
    setMoreNotesAlerts(state, action: PayloadAction<NotificationResponse>) {
      const { payload } = action;
      state.notesPage += 1;
      state.notes = state.notes.concat(payload?.notifications);
    },
    setNotesMessage(state, action: PayloadAction<string>) {
      state.notesLoading = false;
      state.notesEnded = true;
      state.notesMessage = action.payload;
    },
    setNotesEnded(state) {
      state.notesEnded = true;
      state.notesLoading = false;
    },
    reset: (state) => {
      // Preserve feed-related state
      const preservedState = {
        feedPeopleNotifications: state.feedPeopleNotifications,
        feedNotificationsEnded: state.feedNotificationsEnded,
        feedNotificationsPage: state.feedNotificationsPage,
        feedNotificationsLoading: state.feedNotificationsLoading,
        feedPeopleMessage: state.feedPeopleMessage,
      };
      // Reset everything else
      return {
        ...initialState,
        ...preservedState,
      };
    },
  },
});

export const { actions, reducer, name: sliceKey } = AlertsSlice;
